import styled from 'styled-components';

import { formatPercentage, formatResponseTime } from 'components/insights/formatting';
import CircularProgress from 'components/shared/CircularProgress';
import { InboundMetricDrillKey, useInboundReferralsInsightsActions } from 'stores/inboundReferralsInsightsStore';
import { Label } from 'styles/typography';
import ChevronRightBold from 'svg/ChevronRightBold';

type Props = {
  acceptanceRate: number | null;
  denialRate: number | null;
  selectedRate: number | null;
  notSelectedRate: number | null;
  admittedRate: number | null;
  avgTimeToDecisionSeconds: number | null;
  totalReferrals: number;
  acceptedCount: number;
  deniedCount: number;
  selectedCount: number;
  notSelectedCount: number;
  admittedCount: number;
  withSelectionCount: number;
  loading: boolean;
};

type TileConfig = {
  label: string;
  value: string;
  color: 'green' | 'red' | 'default';
  subtitle?: string;
  // When set, the tile opens that metric drill on click. Only wired for drills that are built.
  metric?: InboundMetricDrillKey;
};

export default function InboundReferralHighlightMetrics({
  acceptanceRate,
  denialRate,
  selectedRate,
  notSelectedRate,
  admittedRate,
  avgTimeToDecisionSeconds,
  totalReferrals,
  acceptedCount,
  deniedCount,
  selectedCount,
  notSelectedCount,
  admittedCount,
  withSelectionCount,
  loading,
}: Props) {
  const { openMetricSlideout } = useInboundReferralsInsightsActions();

  if (loading) {
    return (
      <MetricsContainer>
        <StyledProgress />
      </MetricsContainer>
    );
  }

  const tiles: TileConfig[] = [
    {
      label: 'Acceptance Rate',
      value: formatPercentage(acceptanceRate),
      color: acceptanceRate == null ? 'default' : 'green',
      subtitle: `${acceptedCount} of ${totalReferrals} referrals received`,
      metric: 'accepted',
    },
    {
      label: 'Denial Rate',
      value: formatPercentage(denialRate),
      color: denialRate == null ? 'default' : 'red',
      subtitle: `${deniedCount} of ${totalReferrals} referrals received`,
      metric: 'denied',
    },
    {
      label: 'Selected Rate',
      value: formatPercentage(selectedRate),
      color: 'default',
      subtitle: `${selectedCount} of ${withSelectionCount} referrals closed`,
      metric: 'selected',
    },
    {
      label: 'Not Selected Rate',
      value: formatPercentage(notSelectedRate),
      color: 'default',
      subtitle: `${notSelectedCount} of ${withSelectionCount} referrals closed`,
      metric: 'notSelected',
    },
    {
      label: 'Admission Rate',
      value: formatPercentage(admittedRate),
      color: 'default',
      subtitle: `${admittedCount} of ${selectedCount} selected`,
      metric: 'admitted',
    },
    {
      label: 'Avg. Time to Facility Decision',
      value: formatResponseTime(avgTimeToDecisionSeconds),
      color: 'default',
    },
  ];

  return (
    <MetricsContainer>
      {tiles.map((tile) => (
        <MetricTile
          key={tile.label}
          $drillable={!!tile.metric}
          onClick={tile.metric ? () => openMetricSlideout(tile.metric as InboundMetricDrillKey) : undefined}>
          <MetricContent>
            <MetricLabel>{tile.label}</MetricLabel>
            <MetricValue $color={tile.color}>{tile.value}</MetricValue>
            {tile.subtitle && <MetricSubtitle>{tile.subtitle}</MetricSubtitle>}
          </MetricContent>
          {tile.metric && (
            <ChevronWrapper>
              <ChevronRightBold />
            </ChevronWrapper>
          )}
        </MetricTile>
      ))}
    </MetricsContainer>
  );
}

const MetricsContainer = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-width: 320px;
  border-left: 1px solid ${({ theme }) => theme.colors.black10};

  @media (max-width: 800px) {
    border-left: none;
    gap: 8px;
    padding: 0 12px 24px;
  }
`;

const MetricTile = styled.div<{ $drillable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
  cursor: ${({ $drillable }) => ($drillable ? 'pointer' : 'default')};

  &:nth-child(even) {
    border-left: 1px solid ${({ theme }) => theme.colors.black10};
  }

  &:nth-child(-n + 2) {
    border-top: none;
  }

  @media (max-width: 800px) {
    padding: 16px 24px;
    background-color: #f2f3f4;
    border-radius: 4px;
    border: none;

    &:nth-child(even) {
      border-left: none;
    }
  }
`;

const MetricContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const ChevronWrapper = styled.div`
  color: ${({ theme }) => theme.colors.black75};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const MetricLabel = styled(Label)`
  display: block;
  color: ${({ theme }) => theme.colors.black75};
`;

const MetricValue = styled.div<{ $color: 'green' | 'red' | 'default' }>`
  font-size: 20px;
  font-weight: var(--font-weight-medium);
  line-height: 24px;
  color: ${({ $color, theme }) => {
    if ($color === 'green') return theme.colors.green100;
    if ($color === 'red') return theme.colors.red100;
    return theme.colors.black100;
  }};
`;

const MetricSubtitle = styled(Label)`
  color: ${({ theme }) => theme.colors.black50};
`;

const StyledProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.blue100};
  margin: auto;
`;
